import { Erod, Ty } from '@cotera/era';

/**
 * Reads a tool's Erod input schema into the shapes the tool-call node editor
 * needs: the list of top-level params, how to edit each one, and a JSON
 * template for the raw-JSON fallback.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Schema = Erod.Def<any>;

function emptyBaseValue(schema: Schema): unknown {
  const ty = schema.ty.ty;
  if (ty.k === 'enum') {
    return ty.t[0] ?? '';
  }
  if (ty.k === 'primitive') {
    switch (ty.t) {
      case 'boolean':
        return false;
      case 'int':
      case 'float':
        return 0;
      default:
        return '';
    }
  }
  return null;
}

/** A JSON skeleton matching the schema, for the raw-JSON fallback editor. */
export function jsonTemplateOfSchema(schema: Schema): unknown {
  const shape = schema.shape;
  switch (shape.t) {
    case 'lazy':
      return jsonTemplateOfSchema(shape.get());
    case 'discriminatedUnion': {
      const first = shape.options[0];
      return first !== undefined ? jsonTemplateOfSchema(first) : {};
    }
    case 'struct': {
      const out: Record<string, unknown> = {};
      for (const [name, fieldSchema] of Object.entries(shape.fieldsSchema)) {
        out[name] = jsonTemplateOfSchema(fieldSchema);
      }
      return out;
    }
    case 'array':
      // Match sampleJsonFromOutputShape: one element so nested fields are visible.
      return [jsonTemplateOfSchema(shape.elementSchema)];
    case 'base':
      return emptyBaseValue(schema);
    default:
      // record / recordWithKey: a free-form map.
      return {};
  }
}

/** A top-level tool input parameter, resolved from the tool's input schema. */
export type ToolInputField = {
  readonly name: string;
  readonly schema: Schema;
  readonly required: boolean;
  readonly description: string | null;
  /** Human-readable type (e.g. `string`, `timestamp`), for tooltips/hints. */
  readonly typeLabel: string;
};

/**
 * Top-level tool input parameters (name + schema + required + description),
 * unwrapping `lazy`/`discriminatedUnion` wrappers. Empty for non-struct schemas.
 * `descriptions` (from the tool API) supplies field hints that the reconstructed
 * schema no longer carries.
 */
export function listStructFields(
  schema: Schema,
  descriptions: Record<string, string> = {}
): ToolInputField[] {
  const shape = schema.shape;
  switch (shape.t) {
    case 'lazy':
      return listStructFields(shape.get(), descriptions);
    case 'discriminatedUnion': {
      const first = shape.options[0];
      return first !== undefined ? listStructFields(first, descriptions) : [];
    }
    case 'struct':
      return Object.entries(shape.fieldsSchema).map(([name, fieldSchema]) => ({
        name,
        schema: fieldSchema,
        required: !fieldSchema.isNullable,
        description: descriptions[name] ?? fieldSchema.description ?? null,
        typeLabel: Ty.displayTy(fieldSchema.ty),
      }));
    default:
      return [];
  }
}

/**
 * How a tool input field should be edited inline. Scalars get a native control;
 * anything else (`struct`/`array`/records) falls back to a JSON editor.
 */
export type ToolInputFieldKind =
  | { readonly kind: 'string' }
  | { readonly kind: 'number'; readonly integer: boolean }
  | { readonly kind: 'boolean' }
  | { readonly kind: 'enum'; readonly options: readonly string[] }
  | { readonly kind: 'timestamp' }
  | { readonly kind: 'complex' };

/** Classify a field schema so the editor can pick the right input control. */
export function classifyToolInputField(schema: Schema): ToolInputFieldKind {
  const shape = schema.shape;
  if (shape.t === 'lazy') {
    return classifyToolInputField(shape.get());
  }
  if (shape.t !== 'base') {
    return { kind: 'complex' };
  }
  const ty = schema.ty.ty;
  if (ty.k === 'enum') {
    return { kind: 'enum', options: ty.t };
  }
  if (ty.k === 'primitive') {
    switch (ty.t) {
      case 'string':
        return { kind: 'string' };
      case 'int':
        return { kind: 'number', integer: true };
      case 'float':
        return { kind: 'number', integer: false };
      case 'boolean':
        return { kind: 'boolean' };
      case 'timestamp':
        return { kind: 'timestamp' };
      default:
        return { kind: 'complex' };
    }
  }
  return { kind: 'complex' };
}
